// src/admin/pages/customers/customerService.ts
// Customers who registered an account on the client website (Sign Up).
// Separate from `userClaims` (admin/staff accounts) — see admin/src/admin/pages/users.
import {
  collection,
  doc,
  updateDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

export const CUSTOMERS_COLLECTION = 'customers';

export interface Customer {
  uid: string;
  fullName: string;
  email: string;
  phone?: string;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface PaginatedCustomersResult {
  customers: Customer[];
  totalCount: number;
}

export interface CustomerStats {
  totalCustomers: number;
  activeCustomers: number;
  deactivatedCustomers: number;
}

export class CustomerService {
  static async getCustomerStats(): Promise<CustomerStats> {
    const collectionRef = collection(db, CUSTOMERS_COLLECTION);

    const totalSnapshot = await getCountFromServer(collectionRef);
    const totalCustomers = totalSnapshot.data().count;

    const activeSnapshot = await getCountFromServer(query(collectionRef, where('isActive', '==', true)));
    const activeCustomers = activeSnapshot.data().count;

    return {
      totalCustomers,
      activeCustomers,
      deactivatedCustomers: totalCustomers - activeCustomers,
    };
  }

  static async getCustomersPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    statusFilter?: boolean
  ): Promise<PaginatedCustomersResult> {
    try {
      const customers: Customer[] = [];
      const collectionRef = collection(db, CUSTOMERS_COLLECTION);

      const filterConstraints: any[] = [];
      if (statusFilter !== undefined) {
        filterConstraints.push(where('isActive', '==', statusFilter));
      }

      const orderConstraint = orderBy('createdAt', 'desc');

      const countSnapshot = await getCountFromServer(query(collectionRef, ...filterConstraints));
      const totalCount = countSnapshot.data().count;

      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const customer: Customer = {
          uid: docSnap.id,
          fullName: data.fullName || '',
          email: data.email || '',
          phone: data.phone || '',
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const q = searchQuery.toLowerCase();
          const matches =
            customer.fullName.toLowerCase().includes(q) ||
            customer.email.toLowerCase().includes(q) ||
            (customer.phone || '').toLowerCase().includes(q);
          if (matches) customers.push(customer);
        } else {
          customers.push(customer);
        }
      });

      return { customers, totalCount };
    } catch (error: any) {
      console.error('Error fetching paginated customers:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to access customer data.');
      }
      throw error;
    }
  }

  static async toggleCustomerStatus(uid: string, isActive: boolean): Promise<void> {
    try {
      const customerRef = doc(db, CUSTOMERS_COLLECTION, uid);
      await updateDoc(customerRef, {
        isActive,
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error toggling customer status:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to update customer status');
      }
      throw error;
    }
  }
}
